import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check, Phone, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import { ALL_TOURS, PICKUP_LOCATIONS, RIDER_LEVELS, getTourBySlug } from "../data/experiences";
import { formatPrice, shouldShowCurrencyNote } from "../i18n/pricing";
import { LocalizedLink } from "../lib/i18nRouting";
import SEO from "../components/site/SEO";
import Reveal from "../components/Reveal";

const TIME_OPTIONS = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const initialState = {
  experience_slug: "",
  preferred_date: "",
  preferred_time: "",
  number_of_riders: 2,
  pickup_location: PICKUP_LOCATIONS[0],
  rider_level: RIDER_LEVELS[0],
  height: 175,
  message: "",
  full_name: "",
  email: "",
  phone: "",
};

export default function Booking() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [params] = useSearchParams();
  const presetSlug = params.get("experienta") || "";
  const [form, setForm] = useState({ ...initialState, experience_slug: presetSlug });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const selectedExp = useMemo(() => getTourBySlug(form.experience_slug), [form.experience_slug]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When the submit succeeds and we swap to the success screen, force scroll
  // to the top so the confirmation is immediately visible.
  useEffect(() => {
    if (done) window.scrollTo({ top: 0, behavior: "instant" });
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.experience_slug) return toast.error(t("booking.toast.select_experience"));
    if (!form.preferred_date || !form.full_name || !form.email || !form.phone) {
      return toast.error(t("booking.toast.fill_required"));
    }
    setSubmitting(true);
    try {
      const exp = getTourBySlug(form.experience_slug);
      const payload = {
        ...form,
        preferred_time: form.preferred_time || null,
        lang,
        experience_name: t(`tours.${exp.slug}.name`, { lng: "ro" }) || exp.slug,
        number_of_riders: Number(form.number_of_riders),
        height: Number(form.height),
      };
      const res = await api.post("/bookings", payload);
      if (res && res.data && res.data.id) {
        setDone(true);
        toast.success(t("booking.toast.success"));
      } else {
        setDone(true);
        toast.success(t("booking.toast.success"));
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("booking.toast.validation_error"));
      } else {
        setDone(true);
        toast.success(t("booking.toast.success_soft"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-bone min-h-screen pt-32 pb-32" data-testid="booking-success">
        <SEO titleKey="rezervare.title" descKey="rezervare.description" path="/rezervare" />
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <div className="w-16 h-16 mx-auto bg-forest text-bone flex items-center justify-center">
            <Check className="w-8 h-8" />
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-forest mt-10 leading-tight">
            {t("booking.success_title_a")} <span className="italic">{t("booking.success_title_b")}</span>
          </h1>
          <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed">{t("booking.success_body")}</p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <LocalizedLink to="/" className="btn-lift inline-flex items-center justify-center gap-2 bg-forest text-bone px-8 py-4 text-xs uppercase tracking-wider-3">
              {t("booking.success_home")}
            </LocalizedLink>
            <a href="tel:0752354222" className="btn-lift inline-flex items-center justify-center gap-2 border border-forest text-forest px-8 py-4 text-xs uppercase tracking-wider-3">
              <Phone className="w-4 h-4" /> 0752 354 222
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bone min-h-screen" data-testid="booking-page">
      <SEO titleKey="rezervare.title" descKey="rezervare.description" path="/rezervare" />
      <section className="pt-32 lg:pt-44 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-xs uppercase tracking-wider-3 text-moss mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-moss" />
              {t("booking.eyebrow")}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-forest leading-[1.02]">
              {t("booking.title_a")} <span className="italic">{t("booking.title_b")}</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-muted-foreground text-base md:text-lg leading-relaxed">
              {t("booking.intro")}
            </p>
            {shouldShowCurrencyNote(lang) && (
              <p className="mt-4 text-xs uppercase tracking-wider-3 text-moss" data-testid="booking-currency-note">
                {t("booking.currency_note")}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <form onSubmit={submit} className="md:col-span-8 space-y-10" data-testid="booking-form">
            <FieldGroup label={t("booking.step_experience")}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ALL_TOURS.map((e) => (
                  <button
                    type="button"
                    key={e.slug}
                    data-testid={`exp-option-${e.slug}`}
                    onClick={() => set("experience_slug", e.slug)}
                    className={`text-left p-5 border transition-all ${
                      form.experience_slug === e.slug ? "border-forest bg-forest text-bone" : "border-bone-deep bg-white hover:border-forest"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider-3 opacity-70">
                      {(e.category === "oradea" ? "Oradea" : "Apuseni")} · {t(`tours.${e.slug}.duration`)}
                    </div>
                    <div className="font-display text-xl mt-1">{t(`tours.${e.slug}.name`)}</div>
                    <div className="text-xs mt-2 opacity-80">{formatPrice(e.price, lang, e.slug)} {t("common.per_person")}</div>
                  </button>
                ))}
              </div>
            </FieldGroup>

            <FieldGroup label={t("booking.step_details")}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label={t("booking.field.date")}>
                  <input type="date" required value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="input-preferred-date" className="input" />
                </Field>
                <Field label={t("booking.field.time")}>
                  <select value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} data-testid="select-preferred-time" className="input">
                    <option value="">{t("booking.field.time_any")}</option>
                    {TIME_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="block mt-2 text-xs text-muted-foreground" data-testid="preferred-time-note">{t("booking.field.time_note")}</span>
                </Field>
                <Field label={t("booking.field.riders")}>
                  <input type="number" min={1} max={20} required value={form.number_of_riders} onChange={(e) => set("number_of_riders", e.target.value)} data-testid="input-riders" className="input" />
                </Field>
                <Field label={t("booking.field.pickup")}>
                  <select required value={form.pickup_location} onChange={(e) => set("pickup_location", e.target.value)} data-testid="select-pickup" className="input">
                    {PICKUP_LOCATIONS.map((l) => <option key={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label={t("booking.field.level")}>
                  <select required value={form.rider_level} onChange={(e) => set("rider_level", e.target.value)} data-testid="select-level" className="input">
                    {RIDER_LEVELS.map((l) => <option key={l} value={l}>{t(`booking.levels.${l}`)}</option>)}
                  </select>
                </Field>
                <Field label={t("booking.field.height")}>
                  <input type="number" min={120} max={230} required value={form.height} onChange={(e) => set("height", e.target.value)} data-testid="input-height" className="input" />
                </Field>
              </div>
            </FieldGroup>

            <FieldGroup label={t("booking.step_you")}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label={t("booking.field.name")}>
                  <input type="text" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="input-name" className="input" />
                </Field>
                <Field label={t("booking.field.email")}>
                  <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" className="input" />
                </Field>
                <Field label={t("booking.field.phone")} className="md:col-span-2">
                  <input type="tel" required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" className="input" />
                </Field>
              </div>
            </FieldGroup>

            <FieldGroup label={t("booking.step_message")}>
              <textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={t("booking.message_placeholder")} data-testid="input-message" className="input resize-none" />
            </FieldGroup>

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-booking"
              className="btn-lift w-full md:w-auto inline-flex items-center justify-center gap-3 bg-forest text-bone px-10 py-5 text-xs uppercase tracking-wider-3 disabled:opacity-60 hover:bg-forest-deep"
            >
              {submitting ? t("booking.submitting") : t("booking.submit")} <ArrowUpRight className="w-4 h-4" />
            </button>
          </form>

          <aside className="md:col-span-4">
            <Reveal className="md:sticky md:top-28 space-y-8">
              {selectedExp && (
                <div className="border border-bone-deep bg-white">
                  <div className="aspect-[4/3] overflow-hidden bg-stone-warm">
                    <img src={selectedExp.image} alt={t(`tours.${selectedExp.slug}.name`)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <div className="text-[10px] uppercase tracking-wider-3 text-terracotta">{t("booking.selection_label")}</div>
                    <div className="font-display text-2xl text-forest mt-1">{t(`tours.${selectedExp.slug}.name`)}</div>
                    <div className="text-xs uppercase tracking-wider-2 text-moss mt-2">
                      {t(`tours.${selectedExp.slug}.duration`)} · {formatPrice(selectedExp.price, lang, selectedExp.slug)}
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-forest text-bone p-6">
                <div className="text-xs uppercase tracking-wider-3 opacity-70 mb-4">{t("booking.help_title")}</div>
                <a href="tel:0752354222" className="flex items-center gap-3 link-underline"><Phone className="w-4 h-4" /> 0752 354 222</a>
                <a href="mailto:contact@apuseniactive.ro" className="flex items-center gap-3 mt-3 link-underline"><Mail className="w-4 h-4" /> contact@apuseniactive.ro</a>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%; background: white; border: 1px solid hsl(var(--border));
          padding: 14px 16px; font-family: 'Outfit', sans-serif; font-size: 14px;
          color: hsl(var(--foreground)); outline: none;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .input:focus { border-color: hsl(var(--forest)); }
      `}</style>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <Reveal>
      <div className="text-xs uppercase tracking-wider-3 text-moss mb-5">{label}</div>
      {children}
    </Reveal>
  );
}
function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] uppercase tracking-wider-3 text-moss block mb-2">{label}</span>
      {children}
    </label>
  );
}
